import {
  ArrowsAltOutlined,
  DownOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { Button, Card, Dropdown, Menu, Popover, Space, Spin } from "antd";
import moment from "moment";
import { useRef, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as ReTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { TimePeriod } from "../../lib/discordTypes";
import { useEndTime, useStartTime } from "../DateContext/DateContext";
import useNewMembersPeriod from "./useNewMembersPeriod";

/**
 * NewMembersCard props
 * span: the number of columns we want the card to span in the page layout
 * expandNewMembers: wether the component is expanded or not
 * setExpandNewMembers: state function to expand the card in the parent
 */
interface Props {
  span: number;
  expandNewMembers: boolean;
  setExpandNewMembers: React.Dispatch<React.SetStateAction<boolean>>;
}

function NewMembersCard(props: Props) {
  const { expandNewMembers, setExpandNewMembers } = props;
  const { startTime, setStartTime } = useStartTime();
  const { endTime, setEndTime } = useEndTime();

  const [timePeriod, setTimePeriod] = useState<TimePeriod>(TimePeriod.Week);
  const interval = useRef(TimePeriod.Week);

  const { loading, membersMonth, membersWeek } = useNewMembersPeriod(
    startTime,
    endTime,
    timePeriod
  );

  const start = moment(startTime);
  const end = moment(endTime);
  const numberOfWeeks = moment.duration(end.diff(start)).asWeeks();
  const numberOfMonths = moment.duration(end.diff(start)).asMonths();

  function handleMenuClick(e: any) {
    if (e.key === "1") {
      return;
    }
    if (e.key === "2") {
      setTimePeriod(TimePeriod.Week);
    }
    if (e.key === "3") {
      setTimePeriod(TimePeriod.Month);
    }
  }

  const menu = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="2">{TimePeriod.Week}</Menu.Item>
      {numberOfMonths >= 1 ? (
        <Menu.Item key="3">{TimePeriod.Month}</Menu.Item>
      ) : null}
    </Menu>
  );

  const popupContent = (text: string) => (
    <div>
      <p>{text}</p>
    </div>
  );

  return (
    <Card
      style={{ marginBottom: 40 }}
      title="New members"
      bordered={false}
      extra={
        <Space>
          <p style={{ marginTop: 10 }}>Group by</p>
          <Dropdown className="joyride-date" overlay={menu}>
            <Button>
              {timePeriod} <DownOutlined />
            </Button>
          </Dropdown>
          <Button onClick={() => setExpandNewMembers(!expandNewMembers)}>
            <div className="-mt-1">
              <ArrowsAltOutlined />
            </div>
          </Button>
          <Popover
            content={popupContent("New members, grouped by week or month.")}
            className="ml-3"
          >
            <InfoCircleOutlined />
          </Popover>
        </Space>
      }
    >
      <Spin spinning={loading}>
        <div
          style={{
            width: "100%",
            height: expandNewMembers ? "500px" : "250px",
          }}
        >
          <ResponsiveContainer>
            <LineChart
              width={430}
              data={timePeriod === TimePeriod.Week ? membersWeek : membersMonth}
              margin={{
                top: 5,
                right: 5,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="date_joined"
                tickFormatter={(date) => {
                  if (timePeriod === TimePeriod.Month) {
                    return moment(date).format("MM/YYYY");
                  }
                  return moment(date).format("MM/DD");
                }}
              />
              <YAxis domain={["auto", "auto"]} />
              <Legend />
              <ReTooltip />
              <Line
                type="monotone"
                dataKey="count"
                name="# of total members"
                stroke="#8884d8"
                activeDot={{ r: 8 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Spin>
    </Card>
  );
}

export default NewMembersCard;
